<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { get } from 'svelte/store';
	import Fa from 'svelte-fa';
	import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
	import { navbarSlide, heroImageVariant } from '$lib/shared/lib/animations';
	import { initMotionPreferences, prefersReducedMotion } from '$lib/shared/lib/motionPreferences';
	import MarketingButton from '$lib/shared/ui/MarketingButton.svelte';

	import hero1 from '$img/hero/hero-1.jpg';
	import hero2 from '$img/hero/hero-2.jpg';
	import hero3 from '$img/hero/hero-3.jpg';
	import hero4 from '$img/hero/hero-4.jpg';
	import hero5 from '$img/hero/hero-5.jpg';
	import hero6 from '$img/hero/hero-6.jpg';

	import { fade } from 'svelte/transition';

	const heroImages = [hero1, hero2, hero3, hero4, hero5, hero6];

	let currentImageIndex = 0;
	let intervalId: ReturnType<typeof setInterval> | null = null;
	let paused = false;

	initMotionPreferences();

	function startRotation() {
		stopRotation();
		intervalId = setInterval(() => {
			currentImageIndex = (currentImageIndex + 1) % heroImages.length;
		}, 4000);
	}

	function stopRotation() {
		if (intervalId) {
			clearInterval(intervalId);
			intervalId = null;
		}
	}

	function togglePaused() {
		paused = !paused;
		if (paused) stopRotation();
		else startRotation();
	}

	onMount(() => {
		// Honour reduced-motion: keep the first image static, no auto-rotation.
		if (get(prefersReducedMotion)) {
			paused = true;
			return;
		}
		startRotation();
	});

	onDestroy(stopRotation);

	// Smooth-scroll to a section, accounting for the sticky header height so it
	// stops at the section top instead of overshooting (matches the top-bar nav).
	function scrollToSection(event: MouseEvent, id: string) {
		event.preventDefault();
		const target = document.getElementById(id);
		if (!target) return;
		const headerOffset = document.getElementById('header')?.offsetHeight ?? 0;
		const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 24;
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		window.scrollTo({ top: Math.max(top, 0), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
	}
</script>

<div class="relative h-full w-full md:w-auto">
	<div
		class="z-10 mt-12 flex flex-col items-center bg-transparent text-center sm:mt-16"
		in:navbarSlide
	>
		<h1
			class="max-w-4xl px-4 text-5xl/[1.04] font-extrabold tracking-tight text-balance text-surface-950-50 sm:text-6xl/[1.02] md:text-7xl/[1.0]"
		>
			Make every tip land like an
			<span class="text-tertiary-700 dark:text-tertiary-400">event</span>.
		</h1>

		<p
			class="mt-6 max-w-2xl px-6 text-base text-pretty text-secondary-700 sm:mt-7 sm:text-lg dark:text-surface-200"
		>
			Image, text, and voice tips that play live on your stream — paid out daily.
		</p>

		<div class="mt-10 flex flex-col items-center gap-4 sm:mt-12 sm:flex-row">
			<MarketingButton href="/signin" variant="gradient" size="lg">Get Started</MarketingButton>
			<a
				href="#howitworks"
				onclick={(event) => scrollToSection(event, 'howitworks')}
				class="inline-flex items-center justify-center gap-2 rounded-full border border-surface-400-600 px-6 py-3 text-lg font-semibold text-surface-900-100 transition-colors hover:bg-surface-100-900 focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-50 focus-visible:outline-none dark:focus-visible:ring-offset-surface-950"
			>
				See how it works
			</a>
		</div>

		<div class="relative mt-14 w-full max-w-3xl px-4 sm:mt-20 sm:px-0" in:heroImageVariant>
			<div
				class="bg-brand-gradient pointer-events-none absolute inset-x-0 -inset-y-4 -z-10 rounded-[2.5rem] opacity-25 blur-3xl dark:opacity-40"
				aria-hidden="true"
			></div>
			<div
				class="relative aspect-video w-full overflow-hidden rounded-3xl bg-linear-to-r from-primary-500 to-tertiary-500 p-2 shadow-2xl shadow-primary-500/30"
			>
				<div class="relative h-full w-full overflow-hidden rounded-2xl">
					{#each heroImages as image, index (image)}
						{#if index === currentImageIndex}
							<img
								alt="Supertip hero showcase"
								fetchpriority="high"
								src={image}
								class="pointer-events-none absolute inset-0 h-full w-full border-0 object-cover"
								in:fade={{ duration: 600 }}
								out:fade={{ duration: 600 }}
							/>
						{/if}
					{/each}

					{#if !$prefersReducedMotion}
						<button
							type="button"
							onclick={togglePaused}
							aria-label={paused ? 'Resume showcase slideshow' : 'Pause showcase slideshow'}
							class="absolute right-3 bottom-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface-950/60 text-white transition hover:bg-surface-950/80 focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:outline-none"
						>
							<Fa icon={paused ? faPlay : faPause} />
						</button>
					{/if}
				</div>
			</div>
		</div>
	</div>
</div>
